// iter142 — Ride review bottom sheet (1-5 stars + optional comment).
// No login required; the client_id from localStorage authorises the submit.
// Reused from: (a) Active Ride end button, (b) Ride history "Leave a Review".
import { useEffect, useState } from "react";
import { X, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

function ensureClientId() {
  try {
    let cid = localStorage.getItem("habano.client_id");
    if (!cid) {
      cid = (window.crypto?.randomUUID?.() || `cid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem("habano.client_id", cid);
    }
    return cid;
  } catch { return null; }
}

export default function RideReviewSheet({ isOpen, onClose, rideId, onSubmitted }) {
  const { lang } = useI18n();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setRating(0);
    setComment("");
    setBusy(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  const submit = async () => {
    if (!rideId || rating < 1) return;
    setBusy(true);
    try {
      const cid = ensureClientId();
      const { data } = await api.post(`/rides/${rideId}/review`, {
        rating, comment: comment.trim() || null, client_id: cid,
      });
      toast.success(lang === "es" ? "¡Gracias por tu reseña!" : "Thanks for your review!");
      onSubmitted?.(data?.ride || null);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || (lang === "es" ? "No se pudo enviar" : "Could not submit");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" aria-label="Dismiss" data-testid="ride-review-scrim"
        onClick={onClose}
        className="fixed inset-0 z-[90]" style={{ background: "rgba(0,0,0,0.72)" }} />
      <div data-testid="ride-review-sheet"
        className="fixed left-0 right-0 bottom-0 z-[91]"
        style={{
          background: "#171717", borderTopLeftRadius: 20, borderTopRightRadius: 20,
          borderTop: "1px solid rgba(232,184,75,0.3)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          maxHeight: "85vh", display: "flex", flexDirection: "column",
        }}>
        <div style={{ position: "relative", flex: "0 0 auto" }}>
          <div className="grid place-items-center pt-3 pb-2">
            <span style={{ width: 44, height: 4, borderRadius: 9999, background: "rgba(232,184,75,0.45)" }} />
          </div>
          <div className="px-6 pt-2 pb-3">
            <h2 className="pr-12" data-testid="ride-review-title"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#F5F1E8", lineHeight: 1.25 }}>
              {lang === "es" ? "¿Cómo estuvo tu viaje?" : "How was your ride?"}
            </h2>
            <p className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3" }}>
              {lang === "es" ? "Tu reseña ayuda a otros viajeros." : "Your review helps other travellers."}
            </p>
          </div>
          <button type="button" data-testid="ride-review-close" onClick={onClose} aria-label="Close"
            className="grid place-items-center"
            style={{ position: "absolute", top: 12, right: 14, width: 28, height: 28, borderRadius: "50%",
                     background: "#222", color: "#fff", border: "none", zIndex: 90,
                     boxShadow: "0 2px 8px rgba(0,0,0,0.5)" }}>
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="px-6 pb-3" style={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }}>
          <div className="flex justify-center gap-2 my-2" data-testid="ride-review-stars">
            {[1,2,3,4,5].map((n) => (
              <button key={n} type="button" aria-label={`${n} stars`}
                data-testid={`ride-review-star-${n}`}
                onClick={() => setRating(n)}
                style={{ width: 48, height: 48, display: "grid", placeItems: "center" }}>
                <Star className="w-8 h-8"
                  fill={n <= rating ? "#E8B84B" : "none"}
                  color={n <= rating ? "#E8B84B" : "rgba(232,184,75,0.45)"}
                  strokeWidth={1.5} />
              </button>
            ))}
          </div>
          <label htmlFor="ride-review-comment"
            className="block mt-4 mb-2"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#E8B84B", letterSpacing: "0.04em" }}>
            {lang === "es" ? "Comentario (opcional)" : "Comment (optional)"}
          </label>
          <textarea id="ride-review-comment" data-testid="ride-review-comment"
            value={comment} onChange={(e) => setComment(e.target.value)}
            rows={4} placeholder={lang === "es" ? "Cuéntanos cómo fue…" : "Tell us how it went…"}
            className="w-full outline-none"
            style={{ background: "#0B0B0B", color: "#F5F1E8", border: "1px solid rgba(232,184,75,0.3)",
                     borderRadius: 12, padding: "12px 14px",
                     fontFamily: "'DM Sans', sans-serif", fontSize: 16, resize: "vertical" }} />
        </div>

        <div className="px-6 pt-3" style={{ borderTop: "1px solid rgba(232,184,75,0.15)", flex: "0 0 auto" }}>
          <button type="button" data-testid="ride-review-submit"
            onClick={submit} disabled={busy || rating < 1}
            className="w-full inline-flex items-center justify-center gap-2"
            style={{ height: 52, background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
                     color: "#0B0B0B", border: "none", borderRadius: 12,
                     fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700,
                     opacity: (busy || rating < 1) ? 0.45 : 1,
                     cursor: (busy || rating < 1) ? "not-allowed" : "pointer" }}>
            {busy
              ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />{lang === "es" ? "Enviando…" : "Submitting…"}</>
              : (lang === "es" ? "Enviar reseña" : "Submit review")}
          </button>
        </div>
      </div>
    </>
  );
}
